import React from 'react'
import {
  Box,
  Button,
  Flex,
  Image,
  Text,
} from '../../../../utils/chakraMonomorphic'

import {FaceOffBox} from '../../../../services/coreApi/faceoff'
import copy from './copy.json'

// Box colors, as the local Face Off app draws them. Red is a flip, green an
// accepted match, amber a match recorded under the cut-off. The golden review
// draws its one matched face in yellow.
export const BOX_COLORS = {
  flip: '#e5484d',
  accepted: '#37b26c',
  recorded: '#e0a316',
  matched: '#ffdd33',
}

export interface PhotoBox {
  bbox: FaceOffBox
  color: string
  label?: string
  selected?: boolean
  dim?: boolean
  onClick?: () => void
}

export interface FaceOffPhotoProps {
  src?: string
  alt: string
  boxes: PhotoBox[]
  maxH?: string
  // A colored frame around the photo: the golden review's green or red.
  frameColor?: string
  hideBoxes?: boolean
  onClick?: () => void
  testId: string
}

/**
 * A photo with its face boxes drawn over it. Boxes are relative (0 to 1), so
 * they scale with the image at any size.
 */
export const FaceOffPhoto: React.FC<FaceOffPhotoProps> = ({
  src,
  alt,
  boxes,
  maxH = '420px',
  frameColor,
  hideBoxes,
  onClick,
  testId,
}) => {
  if (!src) {
    return (
      <Flex
        h='160px'
        w='100%'
        bg='var(--fo-panel)'
        alignItems='center'
        justifyContent='center'
        data-testid={`${testId}-placeholder`}>
        <Text fontSize='sm' color='var(--fo-muted)'>
          {copy.photo.missing}
        </Text>
      </Flex>
    )
  }
  return (
    <Box
      position='relative'
      display='inline-block'
      maxW='100%'
      lineHeight={0}
      borderWidth={frameColor ? '4px' : 0}
      borderColor={frameColor}
      borderRadius='md'
      cursor={onClick ? 'zoom-in' : undefined}
      onClick={onClick}
      data-testid={`${testId}-frame`}>
      <Image
        // A new src mounts a new img, so the last photo never shows under
        // this one's boxes while it loads.
        key={src}
        src={src}
        alt={alt}
        maxH={maxH}
        maxW='100%'
        objectFit='contain'
        display='block'
        data-testid={testId}
      />
      {!hideBoxes &&
        boxes.map((b) => (
          <Box
            key={`${b.bbox.left}:${b.bbox.top}:${b.label ?? ''}`}
            position='absolute'
            left={`${b.bbox.left * 100}%`}
            top={`${b.bbox.top * 100}%`}
            width={`${b.bbox.width * 100}%`}
            height={`${b.bbox.height * 100}%`}
            border={`${b.selected ? 4 : 2}px solid ${b.color}`}
            boxShadow={
              b.selected ? '0 0 0 3px #fff' : '0 0 0 1px rgba(0,0,0,.7)'
            }
            opacity={b.dim ? 0.35 : 1}
            cursor={b.onClick ? 'pointer' : undefined}
            onClick={
              b.onClick
                ? (e: React.MouseEvent): void => {
                    e.stopPropagation()
                    b.onClick?.()
                  }
                : undefined
            }
            data-testid={`${testId}-box`}
            data-color={b.color}>
            {b.label && (
              <Text
                position='absolute'
                bottom='100%'
                left={0}
                lineHeight='1.2'
                fontSize='xs'
                px={1}
                bg='rgba(13,15,19,0.75)'
                color={b.selected ? 'white' : b.color}
                whiteSpace='nowrap'>
                {b.label}
              </Text>
            )}
          </Box>
        ))}
    </Box>
  )
}

export interface LightboxSide {
  src: string
  alt: string
}

/**
 * A photo enlarged over the page, with the camper's training photo beside it
 * when `side` is given. B toggles the boxes and Esc closes it; `onKey` gets
 * every other key, so the caller can keep its arrows working.
 */
export const FaceOffLightbox: React.FC<{
  src: string
  alt: string
  boxes: PhotoBox[]
  side?: LightboxSide | null
  onClose: () => void
  onKey?: (event: KeyboardEvent) => void
}> = ({src, alt, boxes, side, onClose, onKey}) => {
  const [hideBoxes, setHideBoxes] = React.useState(false)

  React.useEffect(() => {
    const handle = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose()
      else if (event.key.toLowerCase() === 'b') setHideBoxes((h) => !h)
      else {
        onKey?.(event)
        return
      }
      event.preventDefault()
      event.stopPropagation()
    }
    // Capture, so the page's own keys do not also act on Esc and B.
    window.addEventListener('keydown', handle, true)
    return (): void => window.removeEventListener('keydown', handle, true)
  }, [onClose, onKey])

  return (
    <Flex
      position='fixed'
      inset={0}
      zIndex={1400}
      bg='rgba(0,0,0,.88)'
      alignItems='center'
      justifyContent='center'
      onClick={onClose}
      role='dialog'
      aria-modal='true'
      aria-label={copy.photo.enlarged}
      data-testid='face-off-lightbox'>
      <Flex position='fixed' top={3} right={4} gap={2}>
        <Button
          size='sm'
          onClick={(e: React.MouseEvent): void => {
            e.stopPropagation()
            setHideBoxes((h) => !h)
          }}>
          {hideBoxes ? copy.photo.showBoxes : copy.photo.hideBoxes}
        </Button>
        <Button size='sm' onClick={onClose}>
          {copy.photo.close}
        </Button>
      </Flex>
      <Flex
        gap={4}
        alignItems='center'
        maxW='96vw'
        onClick={(e: React.MouseEvent): void => e.stopPropagation()}>
        <Box flex={side ? '3' : undefined} minW={0} textAlign='center'>
          <FaceOffPhoto
            src={src}
            alt={alt}
            boxes={boxes}
            maxH='90vh'
            hideBoxes={hideBoxes}
            testId='face-off-lightbox-photo'
          />
        </Box>
        {side && (
          <Box flex='1' minW={0} textAlign='center'>
            <FaceOffPhoto
              src={side.src}
              alt={side.alt}
              boxes={[]}
              maxH='90vh'
              testId='face-off-lightbox-side'
            />
            <Text fontSize='sm' color='white' mt={1}>
              {side.alt}
            </Text>
          </Box>
        )}
      </Flex>
    </Flex>
  )
}
